import React, { useEffect, useState, createElement } from 'react';
import { Trophy, Gift, Star, Heart, Clock, Download, Share2, ChevronDown, ChevronUp, ChevronRight, Filter, Search, X, Wand2, User, Film, Image as ImageIcon, Sparkles, Play, Info, Zap, Eye, EyeOff, Shield, Lock } from 'lucide-react';
import { RitualRewards } from '../components/rituals/RitualRewards';
import { Tabs } from './components/Tabs';
import { AchievementsMini } from './components/AchievementsMini';
import { Claimables } from './components/Claimables';
import { Collections } from './components/Collections';
import { History } from './components/History';
// Types for rewards
interface BaseReward {
  id: string;
  title: string;
  description: string;
  dateEarned: Date;
  claimed: boolean;
  type: string;
  imageUrl?: string;
}
interface TokenReward extends BaseReward {
  type: 'token';
  amount: number;
  tokenType: 'desire' | 'love' | 'passion';
}
interface BadgeReward extends BaseReward {
  type: 'badge';
  badgeType: 'gold' | 'silver' | 'bronze' | 'special';
  rarity: 'common' | 'uncommon' | 'rare' | 'legendary';
}
interface SkinReward extends BaseReward {
  type: 'skin';
  angelId: string;
  angelName: string;
  skinName: string;
  rarity: 'common' | 'uncommon' | 'rare' | 'legendary' | 'limited';
}
interface ContentReward extends BaseReward {
  type: 'content';
  contentType: 'image' | 'video' | 'audio';
  premium: boolean;
  angelId?: string;
  angelName?: string;
}
interface LootboxReward extends BaseReward {
  type: 'lootbox';
  lootboxType: 'standard' | 'premium' | 'passion' | 'jackpot';
  opened: boolean;
}
interface AIGeneratedReward extends BaseReward {
  type: 'ai_generated';
  aiType: 'deep_shift' | 'face_shift' | 'fantasy' | 'avatar';
  contentType: 'image' | 'video' | 'gif';
  prompt: string;
  model: string;
  hdEnabled: boolean;
  url: string;
  preserveBackground?: boolean;
  motionSmooth?: boolean;
}
interface DickpicReviewReward extends BaseReward {
  type: 'dickpic_review';
  reviewType: 'personal' | 'detailed' | 'fantasy';
  rating: number;
  feedback: string;
  angelName: string;
  isAIGenerated: boolean;
  originalImageUrl?: string;
  sensitiveContent: boolean;
}
type Reward = TokenReward | BadgeReward | SkinReward | ContentReward | LootboxReward | AIGeneratedReward | DickpicReviewReward;
export const RewardHub: React.FC = () => {
  // State
  const [activeTab, setActiveTab] = useState<'all' | 'tokens' | 'badges' | 'skins' | 'content' | 'lootboxes' | 'ai_generated' | 'dickpic_reviews' | 'wheel'>('all');
  const [activePanelTab, setActivePanelTab] = useState<'achievements' | 'claimables' | 'collections' | 'history'>('achievements');
  const [rewards, setRewards] = useState<Reward[]>([]);
  const [filteredRewards, setFilteredRewards] = useState<Reward[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    claimed: false,
    unclaimed: true,
    newest: true,
    oldest: false,
    rarity: {
      common: true,
      uncommon: true,
      rare: true,
      legendary: true,
      limited: true
    }
  });
  const [selectedReward, setSelectedReward] = useState<Reward | null>(null);
  const [showSensitiveContent, setShowSensitiveContent] = useState(false);
  const [currentRitualId, setCurrentRitualId] = useState<string | null>(null);
  // Mock data for AI generated rewards
  const mockAIRewards: AIGeneratedReward[] = [{
    id: 'ds-1',
    title: 'Beach Sunset Animation',
    description: 'Image transformed into a beautiful beach sunset animation',
    dateEarned: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000),
    claimed: true,
    type: 'ai_generated',
    aiType: 'deep_shift',
    contentType: 'video',
    prompt: 'Transform this beach photo into a stunning sunset with gentle waves and seagulls flying',
    model: 'shifttransformer-v2',
    hdEnabled: true,
    url: 'https://placehold.co/600x400/orange/white?text=Beach+Sunset+Animation',
    preserveBackground: true,
    motionSmooth: true,
    imageUrl: 'https://placehold.co/600x400/orange/white?text=Beach+Sunset+Animation'
  }, {
    id: 'fs-1',
    title: 'Viking Warrior Transformation',
    description: 'Face transformed into a fierce viking warrior',
    dateEarned: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000),
    claimed: true,
    type: 'ai_generated',
    aiType: 'face_shift',
    contentType: 'image',
    prompt: 'Transform my face into a fierce viking warrior with war paint and a braided beard',
    model: 'faceshift-enhanced',
    hdEnabled: true,
    url: 'https://placehold.co/600x400/gray/white?text=Viking+Warrior+Face',
    imageUrl: 'https://placehold.co/600x400/gray/white?text=Viking+Warrior+Face'
  }, {
    id: 'ds-2',
    title: 'Cyberpunk City Animation',
    description: 'Street photo transformed into a futuristic cyberpunk city',
    dateEarned: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000),
    claimed: true,
    type: 'ai_generated',
    aiType: 'deep_shift',
    contentType: 'video',
    prompt: 'Transform this street photo into a futuristic cyberpunk city with neon lights, flying cars, and rain',
    model: 'shifttransformer-v3.1',
    hdEnabled: true,
    url: 'https://placehold.co/600x400/purple/white?text=Cyberpunk+City+Animation',
    preserveBackground: false,
    motionSmooth: true,
    imageUrl: 'https://placehold.co/600x400/purple/white?text=Cyberpunk+City+Animation'
  }, {
    id: 'fs-2',
    title: 'Elf Character Transformation',
    description: 'Face transformed into a mystical elf character',
    dateEarned: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000),
    claimed: true,
    type: 'ai_generated',
    aiType: 'face_shift',
    contentType: 'image',
    prompt: 'Transform my face into a mystical elf character with pointed ears, glowing eyes, and intricate facial markings',
    model: 'faceshift-standard',
    hdEnabled: false,
    url: 'https://placehold.co/600x400/green/white?text=Elf+Character+Face',
    imageUrl: 'https://placehold.co/600x400/green/white?text=Elf+Character+Face'
  }, {
    id: 'ds-3',
    title: 'Underwater World Animation',
    description: 'Photo transformed into an underwater scene with sea creatures',
    dateEarned: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000),
    claimed: true,
    type: 'ai_generated',
    aiType: 'deep_shift',
    contentType: 'video',
    prompt: 'Transform this image into a vibrant underwater world with colorful fish, coral reefs, and gentle water movement',
    model: 'shifttransformer-v2',
    hdEnabled: true,
    url: 'https://placehold.co/600x400/blue/white?text=Underwater+World+Animation',
    preserveBackground: false,
    motionSmooth: true,
    imageUrl: 'https://placehold.co/600x400/blue/white?text=Underwater+World+Animation'
  },
  // Adding Unleashed Fantasies (Scenarios)
  {
    id: 'fantasy-1',
    title: 'Midnight Rendezvous',
    description: 'An intimate fantasy scenario in a moonlit garden',
    dateEarned: new Date(Date.now() - 4 * 24 * 60 * 60 * 1000),
    claimed: true,
    type: 'ai_generated',
    aiType: 'fantasy',
    contentType: 'image',
    prompt: 'Create a romantic fantasy scene in a secret garden under moonlight with glowing flowers',
    model: 'scenariogen-v2',
    hdEnabled: true,
    url: 'https://placehold.co/600x400/purple/white?text=Fantasy+Scenario',
    imageUrl: 'https://placehold.co/600x400/purple/white?text=Fantasy+Scenario'
  }, {
    id: 'fantasy-2',
    title: 'Forbidden Desires',
    description: 'A steamy fantasy scenario in a luxury penthouse',
    dateEarned: new Date(Date.now() - 6 * 24 * 60 * 60 * 1000),
    claimed: true,
    type: 'ai_generated',
    aiType: 'fantasy',
    contentType: 'image',
    prompt: 'Create a luxurious penthouse setting with city lights and intimate atmosphere',
    model: 'scenariogen-v2',
    hdEnabled: true,
    url: 'https://placehold.co/600x400/red/white?text=Fantasy+Scenario',
    imageUrl: 'https://placehold.co/600x400/red/white?text=Fantasy+Scenario'
  },
  // Adding Unleashed Angels (Avatars)
  {
    id: 'avatar-1',
    title: 'Celestial Guardian',
    description: 'Custom angel avatar with ethereal wings and halo',
    dateEarned: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000),
    claimed: true,
    type: 'ai_generated',
    aiType: 'avatar',
    contentType: 'image',
    prompt: 'Create a beautiful angelic avatar with glowing wings, golden halo, and ethereal aura',
    model: 'angelgen-v1',
    hdEnabled: true,
    url: 'https://placehold.co/600x400/gold/white?text=Angel+Avatar',
    imageUrl: 'https://placehold.co/600x400/gold/white?text=Angel+Avatar'
  }, {
    id: 'avatar-2',
    title: 'Mystic Companion',
    description: 'Custom angel avatar with arcane symbols and mystical aura',
    dateEarned: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000),
    claimed: true,
    type: 'ai_generated',
    aiType: 'avatar',
    contentType: 'image',
    prompt: 'Create a mystical angel avatar with arcane symbols, flowing robes, and magical aura',
    model: 'angelgen-v1',
    hdEnabled: true,
    url: 'https://placehold.co/600x400/blue/white?text=Angel+Avatar',
    imageUrl: 'https://placehold.co/600x400/blue/white?text=Angel+Avatar'
  }];
  // Mock data for dickpic reviews
  const mockDickpicReviews: DickpicReviewReward[] = [{
    id: 'review-1',
    title: "Harper's Personal Rating",
    description: "Harper's honest opinion of your recent submission",
    dateEarned: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000),
    claimed: true,
    type: 'dickpic_review',
    reviewType: 'personal',
    rating: 8.5,
    feedback: "I love the lighting in this pic! Great angle that really shows off your size. The slight curve is very appealing. Overall, one of the better pics I've received this week!",
    angelName: 'Harper',
    isAIGenerated: false,
    sensitiveContent: true,
    imageUrl: 'https://placehold.co/200x200/pink/white?text=Harper+Review'
  }, {
    id: 'review-2',
    title: "Sophia's Detailed Feedback",
    description: 'Comprehensive analysis with improvement tips',
    dateEarned: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000),
    claimed: true,
    type: 'dickpic_review',
    reviewType: 'detailed',
    rating: 7.8,
    feedback: 'Good thickness and nice grooming! For better pics, try slightly warmer lighting and a lower angle to create more impressive perspective. Your shape is quite nice, and I appreciate how you took care with the composition. Next time, consider a setting with more interesting background elements.',
    angelName: 'Sophia',
    isAIGenerated: false,
    sensitiveContent: true,
    imageUrl: 'https://placehold.co/200x200/purple/white?text=Sophia+Review'
  }, {
    id: 'review-3',
    title: "Lana's Fantasy Response",
    description: 'Playful and imaginative reaction to your submission',
    dateEarned: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000),
    claimed: true,
    type: 'dickpic_review',
    reviewType: 'fantasy',
    rating: 9.2,
    feedback: "Wow... seeing this made me instantly wet. I couldn't help but imagine how it would feel stretching me out. The veins are so pronounced, and the head has such a perfect shape. I had to take a moment to cool down after seeing this... you've definitely got me fantasizing about all the positions we could try.",
    angelName: 'Lana',
    isAIGenerated: false,
    sensitiveContent: true,
    imageUrl: 'https://placehold.co/200x200/red/white?text=Lana+Fantasy+Review'
  }, {
    id: 'review-4',
    title: 'AI-Enhanced Size Comparison',
    description: 'Computer analysis with percentile ranking',
    dateEarned: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000),
    claimed: true,
    type: 'dickpic_review',
    reviewType: 'detailed',
    rating: 8.7,
    feedback: "Based on our AI analysis, your measurements place you in the top 15% for length and top 22% for girth. The curvature is within ideal range for maximum pleasure. Coloration and vascularity are aesthetically pleasing. Overall score places you in the 'Exceptional' category.",
    angelName: 'SinShift AI',
    isAIGenerated: true,
    sensitiveContent: true,
    imageUrl: 'https://placehold.co/200x200/blue/white?text=AI+Analysis'
  }, {
    id: 'review-5',
    title: "Zoey's Video Reaction",
    description: 'Recorded live reaction with commentary',
    dateEarned: new Date(Date.now() - 4 * 24 * 60 * 60 * 1000),
    claimed: true,
    type: 'dickpic_review',
    reviewType: 'fantasy',
    rating: 8.9,
    feedback: "I recorded my genuine reaction to your pic - you can see how surprised I was! Love the thickness and the way you presented it. The slight upward angle really works for you. Definitely one of the more impressive submissions I've received lately.",
    angelName: 'Zoey',
    isAIGenerated: false,
    sensitiveContent: true,
    imageUrl: 'https://placehold.co/200x200/cyan/white?text=Zoey+Video+Review'
  }, {
    id: 'review-6',
    title: 'AI-Generated Enhancement Preview',
    description: 'See your potential with digital enhancement',
    dateEarned: new Date(Date.now() - 6 * 24 * 60 * 60 * 1000),
    claimed: true,
    type: 'dickpic_review',
    reviewType: 'detailed',
    rating: 7.5,
    feedback: "We've created an AI-enhanced version of your submission to show potential improvements. The enhanced version optimizes lighting, angle, and emphasizes your best features. Swipe to compare original and enhanced versions. The AI suggests this presentation would increase your rating by approximately 15%.",
    angelName: 'SinShift AI',
    isAIGenerated: true,
    sensitiveContent: true,
    originalImageUrl: 'https://placehold.co/200x200/gray/white?text=Original',
    imageUrl: 'https://placehold.co/200x200/green/white?text=Enhanced+Version'
  }];
  // Mock data generation
  useEffect(() => {
    // Generate mock data for other reward types
    const mockRewards: Reward[] = [
    // Token rewards
    {
      id: 'token-1',
      title: 'Daily Login Bonus',
      description: 'Reward for logging in 7 days in a row',
      dateEarned: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000),
      claimed: true,
      type: 'token',
      amount: 50,
      tokenType: 'desire',
      imageUrl: 'https://placehold.co/200x200/pink/white?text=50+Tokens'
    }, {
      id: 'token-2',
      title: 'Ritual Completion Bonus',
      description: 'Completed 3 rituals in one day',
      dateEarned: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000),
      claimed: true,
      type: 'token',
      amount: 100,
      tokenType: 'love',
      imageUrl: 'https://placehold.co/200x200/red/white?text=100+Tokens'
    },
    // Badge rewards
    {
      id: 'badge-1',
      title: 'Social Butterfly',
      description: 'Posted 10 times in the social feed',
      dateEarned: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000),
      claimed: true,
      type: 'badge',
      badgeType: 'silver',
      rarity: 'uncommon',
      imageUrl: 'https://placehold.co/200x200/silver/white?text=Silver+Badge'
    },
    // Skin rewards
    {
      id: 'skin-1',
      title: 'Playful Angel Skin',
      description: 'A playful outfit for your favorite angel',
      dateEarned: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000),
      claimed: false,
      type: 'skin',
      angelId: '1',
      angelName: 'Sophia',
      skinName: 'Playful Summer',
      rarity: 'rare',
      imageUrl: 'https://placehold.co/200x200/cyan/white?text=Angel+Skin'
    },
    // Content rewards
    {
      id: 'content-1',
      title: 'Exclusive Photoshoot',
      description: 'Unlocked exclusive content',
      dateEarned: new Date(Date.now() - 4 * 24 * 60 * 60 * 1000),
      claimed: true,
      type: 'content',
      contentType: 'image',
      premium: true,
      angelId: '2',
      angelName: 'Harper',
      imageUrl: 'https://placehold.co/200x200/purple/white?text=Exclusive+Content'
    },
    // Lootbox rewards
    {
      id: 'lootbox-1',
      title: 'Weekly Premium Lootbox',
      description: 'Your weekly premium rewards box',
      dateEarned: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000),
      claimed: false,
      type: 'lootbox',
      lootboxType: 'premium',
      opened: false,
      imageUrl: 'https://placehold.co/200x200/gold/white?text=Premium+Lootbox'
    },
    // Add the AI generated rewards
    ...mockAIRewards,
    // Add the dickpic reviews
    ...mockDickpicReviews];
    setRewards(mockRewards);
  }, []);
  // Filter rewards based on active tab and search query
  useEffect(() => {
    let filtered = [...rewards];
    // Filter by tab
    if (activeTab !== 'all') {
      filtered = filtered.filter(reward => reward.type === activeTab);
    }
    // Filter by search query
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(reward => reward.title.toLowerCase().includes(query) || reward.description.toLowerCase().includes(query));
    }
    // Apply additional filters
    if (filters.claimed && !filters.unclaimed) {
      filtered = filtered.filter(reward => reward.claimed);
    } else if (!filters.claimed && filters.unclaimed) {
      filtered = filtered.filter(reward => !reward.claimed);
    }
    // Sort by date
    if (filters.newest) {
      filtered.sort((a, b) => b.dateEarned.getTime() - a.dateEarned.getTime());
    } else if (filters.oldest) {
      filtered.sort((a, b) => a.dateEarned.getTime() - b.dateEarned.getTime());
    }
    setFilteredRewards(filtered);
  }, [rewards, activeTab, searchQuery, filters]);
  // Handle tab change
  const handleTabChange = (tab: typeof activeTab) => {
    setActiveTab(tab);
  };
  // Handle search
  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };
  // Handle filter toggle
  const handleFilterToggle = () => {
    setShowFilters(!showFilters);
  };
  // Handle filter changes
  const handleFilterChange = (filterKey: string, value: boolean) => {
    setFilters(prev => ({
      ...prev,
      [filterKey]: value
    }));
  };
  // Handle rarity filter changes
  const handleRarityFilterChange = (rarity: string, value: boolean) => {
    setFilters(prev => ({
      ...prev,
      rarity: {
        ...prev.rarity,
        [rarity]: value
      }
    }));
  };
  // Handle reward selection
  const handleRewardSelect = (reward: Reward) => {
    setSelectedReward(reward);
  };
  // Handle reward close
  const handleCloseRewardDetail = () => {
    setSelectedReward(null);
  };
  // Handle reward claim
  const handleClaimReward = (rewardId: string) => {
    setRewards(prev => prev.map(reward => reward.id === rewardId ? {
      ...reward,
      claimed: true
    } : reward));
  };
  // Handle reward download
  const handleDownloadReward = (reward: Reward) => {
    // In a real app, this would trigger a download
    if ('url' in reward) {
      const a = document.createElement('a');
      a.href = reward.url;
      a.download = `${reward.title.replace(/\s+/g, '-')}.${reward.contentType === 'video' ? 'mp4' : 'jpg'}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    }
  };
  // Handle reward share
  const handleShareReward = (reward: Reward) => {
    // In a real app, this would open a share dialog
    alert(`Sharing ${reward.title}`);
  };
  // Handle navigation
  const handleNavigation = (section: string) => {
    // Navigate to another section
    const event = new CustomEvent('navigate', {
      detail: {
        section
      }
    });
    document.dispatchEvent(event);
  };
  // Toggle sensitive content visibility
  const toggleSensitiveContent = () => {
    setShowSensitiveContent(!showSensitiveContent);
  };
  // Add these useEffect hooks to handle wheel events
  useEffect(() => {
    const handleOpenSpinWheel = (event: any) => {
      console.log('DEBUG: RewardHub received openSpinWheel event', event.detail);
      // Set the active tab to the one containing the wheel
      setActiveTab('wheel');
      // Store the ritual ID if provided
      if (event.detail && event.detail.ritualId) {
        setCurrentRitualId(event.detail.ritualId);
      }
    };
    document.addEventListener('openSpinWheel', handleOpenSpinWheel);
    return () => {
      document.removeEventListener('openSpinWheel', handleOpenSpinWheel);
    };
  }, []);
  useEffect(() => {
    const handleSpinWheel = (event: any) => {
      console.log('DEBUG: RewardHub received spinWheel event', event.detail);
      // First, make sure we're on the wheel tab
      setActiveTab('wheel');
      // Use a short delay to ensure the wheel component is mounted
      setTimeout(() => {
        // Trigger the wheel spin
        if (spinWheelRef && spinWheelRef.current) {
          console.log('DEBUG: Triggering wheel spin via ref');
          spinWheelRef.current.spin();
          // Mark ritual as complete if there's a ritual ID
          if (event.detail && event.detail.ritualId) {
            setTimeout(() => {
              const completeEvent = new CustomEvent('ritualCompleted', {
                detail: {
                  ritualId: event.detail.ritualId,
                  type: 'spin_wheel'
                }
              });
              document.dispatchEvent(completeEvent);
            }, 3000); // Wait for wheel to finish spinning
          }
        } else {
          console.log('DEBUG: Spin wheel ref not available');
          // Try alternative method to trigger spin
          const spinButton = document.querySelector('[data-wheel-spin-button="true"]');
          if (spinButton && spinButton instanceof HTMLElement) {
            console.log('DEBUG: Triggering wheel spin via button click');
            spinButton.click();
          } else {
            console.log('DEBUG: Could not find spin button');
          }
        }
      }, 300);
    };
    document.addEventListener('spinWheel', handleSpinWheel);
    return () => {
      document.removeEventListener('spinWheel', handleSpinWheel);
    };
  }, []);
  // Render reward card
  const renderRewardCard = (reward: Reward) => {
    // For dickpic reviews, check if we should show sensitive content
    if (reward.type === 'dickpic_review' && !showSensitiveContent) {
      return <div key={reward.id} className="relative overflow-hidden rounded-lg border border-purple-500/20 dark:border-purple-600/30 bg-white/10 dark:bg-gray-800/20 backdrop-blur-sm shadow-sm hover:shadow-md hover:shadow-purple-500/10 transition-all cursor-pointer group" onClick={() => handleRewardSelect(reward)} data-id="element-23695">
          <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/diamond-upholstery.png')] opacity-5 dark:opacity-10" data-id="element-23696"></div>
          <div className="relative aspect-square overflow-hidden bg-gray-100 dark:bg-gray-800 flex flex-col items-center justify-center p-4 text-center" data-id="element-23697">
            <div className="w-12 h-12 rounded-full bg-pink-100 dark:bg-pink-900/30 flex items-center justify-center mb-3" data-id="element-23698">
              <Lock className="w-6 h-6 text-pink-500" data-id="element-23699" />
            </div>
            <h3 className="font-medium text-gray-900 dark:text-white" data-id="element-23700">
              Sensitive Content
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-2" data-id="element-23701">
              Click to view your dickpic review from{' '}
              {(reward as DickpicReviewReward).angelName}
            </p>
            <button className="mt-4 px-3 py-1.5 bg-pink-100 dark:bg-pink-900/30 text-pink-700 dark:text-pink-300 rounded-full text-sm font-medium" onClick={e => {
            e.stopPropagation();
            toggleSensitiveContent();
          }} data-id="element-23702">
              Show All Reviews
            </button>
          </div>
          <div className="p-3 relative z-10" data-id="element-23703">
            <h3 className="font-bold text-gray-900 dark:text-white line-clamp-1 text-shadow-sm" data-id="element-23704">
              {reward.title}
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 line-clamp-2 mt-1" data-id="element-23705">
              {reward.description}
            </p>
            <div className="flex items-center justify-between mt-2" data-id="element-23706">
              <span className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1" data-id="element-23707">
                <Clock size={12} className="text-purple-400" data-id="element-23708" />
                {reward.dateEarned.toLocaleDateString()}
              </span>
              <span className="text-xs px-1.5 py-0.5 rounded-full bg-gradient-to-r from-pink-400 to-purple-400 text-white shadow-sm" data-id="element-23709">
                Review
              </span>
            </div>
          </div>
        </div>;
    }
    return <div key={reward.id} className="relative overflow-hidden rounded-lg border border-purple-500/20 dark:border-purple-600/30 bg-white/10 dark:bg-gray-800/20 backdrop-blur-sm shadow-sm hover:shadow-md hover:shadow-purple-500/10 transition-all cursor-pointer group" onClick={() => handleRewardSelect(reward)} data-id="element-23710">
        <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/diamond-upholstery.png')] opacity-5 dark:opacity-10" data-id="element-23711"></div>
        <div className="relative aspect-square overflow-hidden" data-id="element-23712">
          <img src={reward.imageUrl || 'https://placehold.co/200x200/gray/white?text=Reward'} alt={reward.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" data-id="element-23713" />
          {/* Glowing overlay effect on hover */}
          <div className="absolute inset-0 bg-gradient-to-t from-purple-500/20 to-pink-500/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-23714"></div>
          {!reward.claimed && <div className="absolute inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center" data-id="element-23715">
              <div className="px-4 py-2 bg-gradient-to-r from-amber-500 to-yellow-500 text-black font-bold rounded-lg shadow-[0_0_15px_rgba(245,158,11,0.5)] animate-pulse-subtle" data-id="element-23716">
                Claim Now
              </div>
            </div>}
          {reward.type === 'ai_generated' && <div className="absolute top-2 left-2 px-2 py-1 bg-gradient-to-r from-purple-500 to-pink-500 text-white text-xs rounded-full flex items-center gap-1 shadow-[0_0_10px_rgba(168,85,247,0.5)]" data-id="element-23717">
              <Wand2 size={10} className="animate-pulse-subtle" data-id="element-23718" />
              <span data-id="element-23719">
                {reward.type === 'ai_generated' && (reward as AIGeneratedReward).aiType === 'deep_shift' ? 'DeepShift' : reward.type === 'ai_generated' && (reward as AIGeneratedReward).aiType === 'face_shift' ? 'FaceShift' : reward.type === 'ai_generated' && (reward as AIGeneratedReward).aiType === 'fantasy' ? 'Fantasy' : 'Angel Avatar'}
              </span>
            </div>}
          {reward.type === 'dickpic_review' && <div className="absolute top-2 left-2 px-2 py-1 bg-gradient-to-r from-pink-500 to-purple-500 text-white text-xs rounded-full flex items-center gap-1 shadow-[0_0_10px_rgba(236,72,153,0.5)]" data-id="element-23720">
              <Star size={10} className="animate-pulse-subtle" data-id="element-23721" />
              <span data-id="element-23722">
                {reward.type === 'dickpic_review' && (reward as DickpicReviewReward).reviewType === 'personal' ? 'Personal' : reward.type === 'dickpic_review' && (reward as DickpicReviewReward).reviewType === 'detailed' ? 'Detailed' : 'Fantasy'}
              </span>
            </div>}
          {reward.type === 'lootbox' && !(reward as LootboxReward).opened && <div className="absolute top-2 right-2 w-6 h-6 flex items-center justify-center bg-gradient-to-r from-red-500 to-pink-500 text-white rounded-full text-xs font-bold animate-pulse shadow-[0_0_10px_rgba(239,68,68,0.5)]" data-id="element-23723">
              !
            </div>}
          {/* Floating particles effect */}
          <div className="absolute inset-0 pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-500" data-id="element-23724">
            {Array.from({
            length: 5
          }).map((_, i) => <div key={`particle-${i}`} className="absolute w-1 h-1 rounded-full bg-white/80 animate-float-particle" style={{
            top: `${Math.random() * 100}%`,
            left: `${Math.random() * 100}%`,
            animationDelay: `${Math.random() * 2}s`,
            animationDuration: `${Math.random() * 3 + 2}s`,
            '--random-x': `${Math.random() * 20 - 10}px`
          } as React.CSSProperties} data-id="element-23725"></div>)}
          </div>
        </div>
        <div className="p-3 relative z-10" data-id="element-23726">
          <h3 className="font-bold text-gray-900 dark:text-white line-clamp-1 text-shadow-sm" data-id="element-23727">
            {reward.title}
          </h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 line-clamp-2 mt-1" data-id="element-23728">
            {reward.description}
          </p>
          <div className="flex items-center justify-between mt-2" data-id="element-23729">
            <span className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1" data-id="element-23730">
              <Clock size={12} className="text-purple-400" data-id="element-23731" />
              {reward.dateEarned.toLocaleDateString()}
            </span>
            {reward.type === 'token' && <span className="text-sm font-medium text-transparent bg-clip-text bg-gradient-to-r from-pink-500 to-purple-500" data-id="element-23732">
                {reward.amount} tokens
              </span>}
            {reward.type === 'badge' && <span className={`text-xs px-1.5 py-0.5 rounded-full ${reward.badgeType === 'gold' ? 'bg-gradient-to-r from-yellow-400 to-amber-500 text-black' : reward.badgeType === 'silver' ? 'bg-gradient-to-r from-gray-300 to-gray-400 text-black' : reward.badgeType === 'bronze' ? 'bg-gradient-to-r from-orange-400 to-amber-600 text-black' : 'bg-gradient-to-r from-purple-400 to-purple-600 text-white'} shadow-sm`} data-id="element-23733">
                {reward.badgeType.charAt(0).toUpperCase() + reward.badgeType.slice(1)}
              </span>}
            {reward.type === 'skin' && <span className={`text-xs px-1.5 py-0.5 rounded-full ${reward.rarity === 'legendary' ? 'bg-gradient-to-r from-yellow-400 to-amber-500 text-black' : reward.rarity === 'rare' ? 'bg-gradient-to-r from-blue-400 to-blue-600 text-white' : reward.rarity === 'uncommon' ? 'bg-gradient-to-r from-green-400 to-green-600 text-white' : reward.rarity === 'limited' ? 'bg-gradient-to-r from-purple-400 to-purple-600 text-white' : 'bg-gradient-to-r from-gray-300 to-gray-500 text-white'} shadow-sm`} data-id="element-23734">
                {reward.rarity.charAt(0).toUpperCase() + reward.rarity.slice(1)}
              </span>}
            {reward.type === 'content' && <span className={`text-xs px-1.5 py-0.5 rounded-full ${reward.premium ? 'bg-gradient-to-r from-purple-400 to-purple-600 text-white' : 'bg-gradient-to-r from-blue-400 to-blue-600 text-white'} shadow-sm`} data-id="element-23735">
                {reward.premium ? 'Premium' : 'Standard'}
              </span>}
            {reward.type === 'lootbox' && <span className={`text-xs px-1.5 py-0.5 rounded-full ${reward.lootboxType === 'premium' ? 'bg-gradient-to-r from-purple-400 to-purple-600 text-white' : reward.lootboxType === 'passion' ? 'bg-gradient-to-r from-red-400 to-red-600 text-white' : reward.lootboxType === 'jackpot' ? 'bg-gradient-to-r from-yellow-400 to-amber-500 text-black' : 'bg-gradient-to-r from-blue-400 to-blue-600 text-white'} shadow-sm`} data-id="element-23736">
                {reward.lootboxType.charAt(0).toUpperCase() + reward.lootboxType.slice(1)}
              </span>}
            {reward.type === 'ai_generated' && <span className={`text-xs px-1.5 py-0.5 rounded-full ${(reward as AIGeneratedReward).contentType === 'video' ? 'bg-gradient-to-r from-blue-400 to-blue-600 text-white' : 'bg-gradient-to-r from-green-400 to-green-600 text-white'} shadow-sm`} data-id="element-23737">
                {(reward as AIGeneratedReward).contentType.charAt(0).toUpperCase() + (reward as AIGeneratedReward).contentType.slice(1)}
              </span>}
            {reward.type === 'dickpic_review' && <span className={`text-xs px-1.5 py-0.5 rounded-full ${(reward as DickpicReviewReward).isAIGenerated ? 'bg-gradient-to-r from-blue-400 to-cyan-400 text-white' : 'bg-gradient-to-r from-pink-400 to-rose-400 text-white'} shadow-sm`} data-id="element-23738">
                {(reward as DickpicReviewReward).rating.toFixed(1)} / 10
              </span>}
          </div>
        </div>
      </div>;
  };
  // Render reward detail
  const renderRewardDetail = () => {
    if (!selectedReward) return null;
    // Special handling for dickpic reviews
    if (selectedReward.type === 'dickpic_review') {
      const review = selectedReward as DickpicReviewReward;
      return <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-lg animate-in fade-in duration-300" data-id="element-23739">
          <div className="relative w-full max-w-4xl max-h-[90vh] overflow-auto animate-in zoom-in-90 duration-300 bg-gradient-to-b from-gray-50/95 to-white/95 dark:from-gray-900/90 dark:to-black/90 backdrop-blur-md rounded-xl shadow-2xl border border-purple-500/20" data-id="element-23740">
            {/* Background pattern */}
            <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/diamond-upholstery.png')] opacity-5 rounded-xl" data-id="element-23741"></div>
            {/* Floating particles */}
            <div className="absolute inset-0 overflow-hidden rounded-xl pointer-events-none" data-id="element-23742">
              {Array.from({
              length: 20
            }).map((_, i) => <div key={`modal-particle-${i}`} className="absolute w-1 h-1 rounded-full bg-pink-400/60 dark:bg-white/60 animate-float-particle" style={{
              top: `${Math.random() * 100}%`,
              left: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 5}s`,
              animationDuration: `${Math.random() * 10 + 10}s`,
              '--random-x': `${Math.random() * 40 - 20}px`
            } as React.CSSProperties} data-id="element-23743"></div>)}
            </div>
            <button className="absolute right-4 top-4 z-10 w-8 h-8 flex items-center justify-center rounded-full bg-gray-200/80 dark:bg-gray-800/80 border border-gray-300/50 dark:border-gray-700/50 backdrop-blur-sm transition-colors hover:bg-gray-300/80 dark:hover:bg-gray-700/80" onClick={handleCloseRewardDetail} data-id="element-23744">
              <X size={18} className="text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white" data-id="element-23745" />
            </button>
            <div className="flex flex-col md:flex-row" data-id="element-23746">
              {/* Left side - image/video */}
              <div className="w-full md:w-1/2 p-6" data-id="element-23747">
                <div className="relative rounded-lg overflow-hidden bg-gray-100/50 dark:bg-gray-900/50 border border-purple-500/20 shadow-[0_0_20px_rgba(168,85,247,0.2)]" data-id="element-23748">
                  {review.sensitiveContent && !showSensitiveContent ? <div className="aspect-video bg-gray-100 dark:bg-gray-900 flex flex-col items-center justify-center p-6 text-center" data-id="element-23749">
                      <div className="w-16 h-16 rounded-full bg-pink-100 dark:bg-pink-900/30 flex items-center justify-center mb-4" data-id="element-23750">
                        <EyeOff className="w-8 h-8 text-pink-500" data-id="element-23751" />
                      </div>
                      <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2" data-id="element-23752">
                        Sensitive Content
                      </h3>
                      <p className="text-gray-600 dark:text-gray-400 mb-4" data-id="element-23753">
                        This review contains sensitive content that is hidden
                        for privacy.
                      </p>
                      <button className="px-4 py-2 bg-gradient-to-r from-pink-500 to-purple-500 text-white rounded-lg text-sm font-medium" onClick={toggleSensitiveContent} data-id="element-23754">
                        Show Sensitive Content
                      </button>
                    </div> : <img src={review.imageUrl || 'https://placehold.co/600x400/gray/white?text=Review'} alt={review.title} className="w-full h-auto" data-id="element-23755" />}
                  {/* Original image if available */}
                  {showSensitiveContent && review.originalImageUrl && <div className="mt-4 p-3 bg-gray-100/70 dark:bg-gray-800/70 backdrop-blur-sm rounded-lg border border-gray-300/50 dark:border-gray-700/50" data-id="element-23756">
                      <div className="flex items-center justify-between mb-2" data-id="element-23757">
                        <h4 className="text-sm font-medium text-gray-900 dark:text-white" data-id="element-23758">
                          Original Submission
                        </h4>
                        <div className="text-xs px-2 py-0.5 rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300" data-id="element-23759">
                          Before Enhancement
                        </div>
                      </div>
                      <div className="rounded-lg overflow-hidden" data-id="element-23760">
                        <img src={review.originalImageUrl} alt="Original submission" className="w-full h-auto" data-id="element-23761" />
                      </div>
                    </div>}
                  {/* AI badge if AI-generated */}
                  {review.isAIGenerated && <div className="absolute top-2 left-2 px-3 py-1.5 bg-gradient-to-r from-blue-500 to-cyan-500 text-white text-sm rounded-full flex items-center gap-1.5 shadow-[0_0_15px_rgba(59,130,246,0.4)]" data-id="element-23762">
                      <Sparkles size={14} className="animate-pulse-subtle" data-id="element-23763" />
                      <span data-id="element-23764">AI Analysis</span>
                    </div>}
                </div>
                {/* Actions */}
                <div className="mt-4 flex gap-2" data-id="element-23765">
                  {!selectedReward.claimed && <button className="flex-1 py-2 px-4 bg-gradient-to-r from-yellow-500 to-amber-500 text-black rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-2 hover:shadow-[0_0_15px_rgba(245,158,11,0.4)] relative overflow-hidden group" onClick={() => handleClaimReward(selectedReward.id)} data-id="element-23766">
                      {/* Button hover effect */}
                      <div className="absolute inset-0 bg-white/20 opacity-0 group-hover:opacity-100 transform translate-x-full group-hover:translate-x-0 transition-all duration-500" data-id="element-23767"></div>
                      <Gift size={16} className="relative z-10" data-id="element-23768" />
                      <span className="relative z-10" data-id="element-23769">Claim Reward</span>
                    </button>}
                  <button className="flex-1 py-2 px-4 bg-gradient-to-r from-gray-200 to-gray-300 hover:from-gray-300 hover:to-gray-400 dark:from-gray-700 dark:to-gray-800 dark:hover:from-gray-600 dark:hover:to-gray-700 rounded-lg text-gray-800 dark:text-white text-sm font-medium transition-colors flex items-center justify-center gap-2 relative overflow-hidden group" onClick={() => handleShareReward(selectedReward)} data-id="element-23770">
                    {/* Button hover effect */}
                    <div className="absolute inset-0 bg-white/10 opacity-0 group-hover:opacity-100 transform translate-x-full group-hover:translate-x-0 transition-all duration-500" data-id="element-23771"></div>
                    <Share2 size={16} className="relative z-10" data-id="element-23772" />
                    <span className="relative z-10" data-id="element-23773">Share</span>
                  </button>
                </div>
              </div>
              {/* Right side - details */}
              <div className="w-full md:w-1/2 p-6 border-t md:border-t-0 md:border-l border-purple-500/20" data-id="element-23774">
                <h2 className="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-gray-900 to-gray-700 dark:from-white dark:to-gray-300 mb-1" data-id="element-23775">
                  {review.title}
                </h2>
                <div className="flex items-center gap-2 mt-1 mb-3" data-id="element-23776">
                  <div className="px-3 py-1 rounded-full bg-gradient-to-r from-pink-100 to-purple-100 dark:from-pink-900/30 dark:to-purple-900/30 text-pink-800 dark:text-pink-300 text-sm font-medium" data-id="element-23777">
                    {review.reviewType.charAt(0).toUpperCase() + review.reviewType.slice(1)}{' '}
                    Review
                  </div>
                  <div className="px-3 py-1 rounded-full bg-gradient-to-r from-yellow-100 to-amber-100 dark:from-yellow-900/30 dark:to-amber-900/30 text-amber-800 dark:text-amber-300 text-sm font-medium flex items-center gap-1" data-id="element-23778">
                    <Star size={14} className="fill-amber-500 text-amber-500" data-id="element-23779" />
                    <span data-id="element-23780">{review.rating.toFixed(1)}/10</span>
                  </div>
                </div>
                <div className="flex items-center gap-2 mb-4" data-id="element-23781">
                  <div className="w-8 h-8 rounded-full bg-pink-100 dark:bg-pink-900/30 flex items-center justify-center" data-id="element-23782">
                    <User size={16} className="text-pink-500" data-id="element-23783" />
                  </div>
                  <div data-id="element-23784">
                    <p className="text-sm font-medium text-gray-900 dark:text-white" data-id="element-23785">
                      Review by {review.angelName}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-23786">
                      {review.dateEarned.toLocaleDateString()}
                    </p>
                  </div>
                </div>
                <div className="p-4 bg-gray-100/70 dark:bg-gray-800/50 backdrop-blur-sm rounded-lg border border-gray-300/50 dark:border-purple-500/20 relative overflow-hidden mb-4" data-id="element-23787">
                  {/* Background glow */}
                  <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/diamond-upholstery.png')] opacity-5" data-id="element-23788"></div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2 relative z-10" data-id="element-23789">
                    Feedback
                  </h3>
                  <p className="text-gray-700 dark:text-gray-300 relative z-10 leading-relaxed" data-id="element-23790">
                    {review.feedback}
                  </p>
                </div>
                {/* Rating breakdown */}
                <div className="p-4 bg-gray-100/70 dark:bg-gray-800/50 backdrop-blur-sm rounded-lg border border-gray-300/50 dark:border-purple-500/20 relative overflow-hidden" data-id="element-23791">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3 relative z-10" data-id="element-23792">
                    Rating Breakdown
                  </h3>
                  <div className="space-y-3 relative z-10" data-id="element-23793">
                    <div data-id="element-23794">
                      <div className="flex justify-between mb-1" data-id="element-23795">
                        <span className="text-sm text-gray-700 dark:text-gray-300" data-id="element-23796">
                          Overall Impression
                        </span>
                        <span className="text-sm font-medium text-gray-900 dark:text-white" data-id="element-23797">
                          {review.rating.toFixed(1)}/10
                        </span>
                      </div>
                      <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden" data-id="element-23798">
                        <div className="h-full bg-gradient-to-r from-pink-500 to-purple-500 rounded-full" style={{
                        width: `${review.rating * 10}%`
                      }} data-id="element-23799"></div>
                      </div>
                    </div>
                    <div data-id="element-23800">
                      <div className="flex justify-between mb-1" data-id="element-23801">
                        <span className="text-sm text-gray-700 dark:text-gray-300" data-id="element-23802">
                          Composition
                        </span>
                        <span className="text-sm font-medium text-gray-900 dark:text-white" data-id="element-23803">
                          {(review.rating * 0.9 + Math.random() * 0.5).toFixed(1)}
                          /10
                        </span>
                      </div>
                      <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden" data-id="element-23804">
                        <div className="h-full bg-gradient-to-r from-blue-500 to-cyan-500 rounded-full" style={{
                        width: `${(review.rating * 0.9 + Math.random() * 0.5) * 10}%`
                      }} data-id="element-23805"></div>
                      </div>
                    </div>
                    <div data-id="element-23806">
                      <div className="flex justify-between mb-1" data-id="element-23807">
                        <span className="text-sm text-gray-700 dark:text-gray-300" data-id="element-23808">
                          Lighting
                        </span>
                        <span className="text-sm font-medium text-gray-900 dark:text-white" data-id="element-23809">
                          {(review.rating * 0.85 + Math.random() * 0.7).toFixed(1)}
                          /10
                        </span>
                      </div>
                      <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden" data-id="element-23810">
                        <div className="h-full bg-gradient-to-r from-amber-500 to-yellow-500 rounded-full" style={{
                        width: `${(review.rating * 0.85 + Math.random() * 0.7) * 10}%`
                      }} data-id="element-23811"></div>
                      </div>
                    </div>
                    <div data-id="element-23812">
                      <div className="flex justify-between mb-1" data-id="element-23813">
                        <span className="text-sm text-gray-700 dark:text-gray-300" data-id="element-23814">
                          Creativity
                        </span>
                        <span className="text-sm font-medium text-gray-900 dark:text-white" data-id="element-23815">
                          {(review.rating * 0.8 + Math.random() * 1).toFixed(1)}
                          /10
                        </span>
                      </div>
                      <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden" data-id="element-23816">
                        <div className="h-full bg-gradient-to-r from-green-500 to-emerald-500 rounded-full" style={{
                        width: `${(review.rating * 0.8 + Math.random() * 1) * 10}%`
                      }} data-id="element-23817"></div>
                      </div>
                    </div>
                  </div>
                </div>
                {/* Privacy notice */}
                <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800/30 rounded-lg flex items-start gap-2" data-id="element-23818">
                  <Shield size={16} className="text-blue-500 mt-0.5 flex-shrink-0" data-id="element-23819" />
                  <div data-id="element-23820">
                    <p className="text-sm font-medium text-blue-800 dark:text-blue-300" data-id="element-23821">
                      Privacy Protected
                    </p>
                    <p className="text-xs text-blue-600 dark:text-blue-400 mt-0.5" data-id="element-23822">
                      Your submissions and reviews are private and encrypted.
                      Only you can see this content.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>;
    }
    return <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-lg animate-in fade-in duration-300" data-id="element-23823">
        <div className="relative w-full max-w-4xl max-h-[90vh] overflow-auto animate-in zoom-in-90 duration-300 bg-gradient-to-b from-gray-50/95 to-white/95 dark:from-gray-900/90 dark:to-black/90 backdrop-blur-md rounded-xl shadow-2xl border border-purple-500/20" data-id="element-23824">
          {/* Background pattern */}
          <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/diamond-upholstery.png')] opacity-5 rounded-xl" data-id="element-23825"></div>
          {/* Floating particles */}
          <div className="absolute inset-0 overflow-hidden rounded-xl pointer-events-none" data-id="element-23826">
            {Array.from({
            length: 20
          }).map((_, i) => <div key={`modal-particle-${i}`} className="absolute w-1 h-1 rounded-full bg-pink-400/60 dark:bg-white/60 animate-float-particle" style={{
            top: `${Math.random() * 100}%`,
            left: `${Math.random() * 100}%`,
            animationDelay: `${Math.random() * 5}s`,
            animationDuration: `${Math.random() * 10 + 10}s`,
            '--random-x': `${Math.random() * 40 - 20}px`
          } as React.CSSProperties} data-id="element-23827"></div>)}
          </div>
          <button className="absolute right-4 top-4 z-10 w-8 h-8 flex items-center justify-center rounded-full bg-gray-200/80 dark:bg-gray-800/80 border border-gray-300/50 dark:border-gray-700/50 backdrop-blur-sm transition-colors hover:bg-gray-300/80 dark:hover:bg-gray-700/80" onClick={handleCloseRewardDetail} data-id="element-23828">
            <X size={18} className="text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white" data-id="element-23829" />
          </button>
          <div className="flex flex-col md:flex-row" data-id="element-23830">
            {/* Left side - image/video */}
            <div className="w-full md:w-1/2 p-6" data-id="element-23831">
              <div className="relative rounded-lg overflow-hidden bg-gray-100/50 dark:bg-gray-900/50 border border-purple-500/20 shadow-[0_0_20px_rgba(168,85,247,0.2)]" data-id="element-23832">
                {selectedReward.type === 'ai_generated' && (selectedReward as AIGeneratedReward).contentType === 'video' ? <video src={(selectedReward as AIGeneratedReward).url} className="w-full h-auto" controls autoPlay loop data-id="element-23833" /> : <img src={selectedReward.imageUrl || 'https://placehold.co/600x400/gray/white?text=Reward'} alt={selectedReward.title} className="w-full h-auto" data-id="element-23834" />}
                {/* Glowing overlay effect */}
                <div className="absolute inset-0 bg-gradient-to-t from-purple-500/10 to-transparent pointer-events-none" data-id="element-23835"></div>
                {selectedReward.type === 'ai_generated' && <div className="absolute top-2 left-2 px-3 py-1.5 bg-gradient-to-r from-purple-500 to-pink-500 text-white text-sm rounded-full flex items-center gap-1.5 shadow-[0_0_15px_rgba(168,85,247,0.4)]" data-id="element-23836">
                    <Wand2 size={14} className="animate-pulse-subtle" data-id="element-23837" />
                    <span data-id="element-23838">
                      {(selectedReward as AIGeneratedReward).aiType === 'deep_shift' ? 'DeepShift' : (selectedReward as AIGeneratedReward).aiType === 'face_shift' ? 'FaceShift' : (selectedReward as AIGeneratedReward).aiType === 'fantasy' ? 'Fantasy' : 'Angel Avatar'}
                    </span>
                  </div>}
              </div>
              {/* Actions */}
              <div className="mt-4 flex gap-2" data-id="element-23839">
                {!selectedReward.claimed && <button className="flex-1 py-2 px-4 bg-gradient-to-r from-yellow-500 to-amber-500 text-black rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-2 hover:shadow-[0_0_15px_rgba(245,158,11,0.4)] relative overflow-hidden group" onClick={() => handleClaimReward(selectedReward.id)} data-id="element-23840">
                    {/* Button hover effect */}
                    <div className="absolute inset-0 bg-white/20 opacity-0 group-hover:opacity-100 transform translate-x-full group-hover:translate-x-0 transition-all duration-500" data-id="element-23841"></div>
                    <Gift size={16} className="relative z-10" data-id="element-23842" />
                    <span className="relative z-10" data-id="element-23843">Claim Reward</span>
                  </button>}
                {selectedReward.type === 'ai_generated' && <>
                    <button className="flex-1 py-2 px-4 bg-gradient-to-r from-gray-200 to-gray-300 hover:from-gray-300 hover:to-gray-400 dark:from-gray-700 dark:to-gray-800 dark:hover:from-gray-600 dark:hover:to-gray-700 rounded-lg text-gray-800 dark:text-white text-sm font-medium transition-colors flex items-center justify-center gap-2 relative overflow-hidden group" onClick={() => handleDownloadReward(selectedReward)} data-id="element-23844">
                      {/* Button hover effect */}
                      <div className="absolute inset-0 bg-white/10 opacity-0 group-hover:opacity-100 transform translate-x-full group-hover:translate-x-0 transition-all duration-500" data-id="element-23845"></div>
                      <Download size={16} className="relative z-10" data-id="element-23846" />
                      <span className="relative z-10" data-id="element-23847">Download</span>
                    </button>
                    <button className="flex-1 py-2 px-4 bg-gradient-to-r from-gray-200 to-gray-300 hover:from-gray-300 hover:to-gray-400 dark:from-gray-700 dark:to-gray-800 dark:hover:from-gray-600 dark:hover:to-gray-700 rounded-lg text-gray-800 dark:text-white text-sm font-medium transition-colors flex items-center justify-center gap-2 relative overflow-hidden group" onClick={() => handleShareReward(selectedReward)} data-id="element-23848">
                      {/* Button hover effect */}
                      <div className="absolute inset-0 bg-white/10 opacity-0 group-hover:opacity-100 transform translate-x-full group-hover:translate-x-0 transition-all duration-500" data-id="element-23849"></div>
                      <Share2 size={16} className="relative z-10" data-id="element-23850" />
                      <span className="relative z-10" data-id="element-23851">Share</span>
                    </button>
                  </>}
              </div>
            </div>
            {/* Right side - details */}
            <div className="w-full md:w-1/2 p-6 border-t md:border-t-0 md:border-l border-purple-500/20" data-id="element-23852">
              <h2 className="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-gray-900 to-gray-700 dark:from-white dark:to-gray-300 mb-1" data-id="element-23853">
                {selectedReward.title}
              </h2>
              <p className="mt-2 text-gray-700 dark:text-gray-300" data-id="element-23854">
                {selectedReward.description}
              </p>
              <div className="mt-4 p-4 bg-gray-100/70 dark:bg-gray-800/50 backdrop-blur-sm rounded-lg border border-gray-300/50 dark:border-purple-500/20 relative overflow-hidden" data-id="element-23855">
                {/* Background glow */}
                <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/diamond-upholstery.png')] opacity-5" data-id="element-23856"></div>
                <div className="flex items-center justify-between mb-2 relative z-10" data-id="element-23857">
                  <span className="text-sm text-gray-600 dark:text-gray-400" data-id="element-23858">
                    Date Earned:
                  </span>
                  <span className="text-sm font-medium text-gray-900 dark:text-white" data-id="element-23859">
                    {selectedReward.dateEarned.toLocaleDateString()}
                  </span>
                </div>
                <div className="flex items-center justify-between mb-2 relative z-10" data-id="element-23860">
                  <span className="text-sm text-gray-600 dark:text-gray-400" data-id="element-23861">
                    Status:
                  </span>
                  <span className={`text-sm font-medium ${selectedReward.claimed ? 'text-green-600 dark:text-green-400' : 'text-amber-600 dark:text-amber-400'}`} data-id="element-23862">
                    {selectedReward.claimed ? 'Claimed' : 'Unclaimed'}
                  </span>
                </div>
                <div className="flex items-center justify-between relative z-10" data-id="element-23863">
                  <span className="text-sm text-gray-600 dark:text-gray-400" data-id="element-23864">
                    Type:
                  </span>
                  <span className="text-sm font-medium text-gray-900 dark:text-white capitalize" data-id="element-23865">
                    {selectedReward.type.replace('_', ' ')}
                  </span>
                </div>
              </div>
              {/* AI-specific details */}
              {selectedReward.type === 'ai_generated' && <div className="mt-4" data-id="element-23866">
                  <h3 className="text-lg font-semibold text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-pink-600 dark:from-purple-400 dark:to-pink-400 mb-2" data-id="element-23867">
                    AI Details
                  </h3>
                  <div className="space-y-3" data-id="element-23868">
                    <div className="p-4 bg-gray-100/70 dark:bg-gray-800/50 backdrop-blur-sm rounded-lg border border-gray-300/50 dark:border-purple-500/20 relative overflow-hidden" data-id="element-23869">
                      {/* Background glow */}
                      <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/diamond-upholstery.png')] opacity-5" data-id="element-23870"></div>
                      <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-2 relative z-10" data-id="element-23871">
                        Prompt
                      </h4>
                      <p className="text-sm text-gray-700 dark:text-gray-300 relative z-10" data-id="element-23872">
                        {(selectedReward as AIGeneratedReward).prompt}
                      </p>
                    </div>
                    <div className="grid grid-cols-2 gap-3" data-id="element-23873">
                      <div className="p-3 bg-gray-100/70 dark:bg-gray-800/50 backdrop-blur-sm rounded-lg border border-gray-300/50 dark:border-purple-500/20 relative overflow-hidden" data-id="element-23874">
                        {/* Background glow */}
                        <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/diamond-upholstery.png')] opacity-5" data-id="element-23875"></div>
                        <div className="flex items-center gap-1.5 mb-1 relative z-10" data-id="element-23876">
                          <Sparkles size={14} className="text-purple-600 dark:text-purple-400 animate-pulse-subtle" data-id="element-23877" />
                          <h4 className="text-sm font-medium text-gray-900 dark:text-white" data-id="element-23878">
                            Model
                          </h4>
                        </div>
                        <p className="text-sm text-gray-700 dark:text-gray-300 relative z-10" data-id="element-23879">
                          {(selectedReward as AIGeneratedReward).model}
                        </p>
                      </div>
                      <div className="p-3 bg-gray-100/70 dark:bg-gray-800/50 backdrop-blur-sm rounded-lg border border-gray-300/50 dark:border-purple-500/20 relative overflow-hidden" data-id="element-23880">
                        {/* Background glow */}
                        <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/diamond-upholstery.png')] opacity-5" data-id="element-23881"></div>
                        <div className="flex items-center gap-1.5 mb-1 relative z-10" data-id="element-23882">
                          <Zap size={14} className="text-amber-600 dark:text-amber-400 animate-pulse-subtle" data-id="element-23883" />
                          <h4 className="text-sm font-medium text-gray-900 dark:text-white" data-id="element-23884">
                            Quality
                          </h4>
                        </div>
                        <p className="text-sm text-gray-700 dark:text-gray-300 relative z-10" data-id="element-23885">
                          {(selectedReward as AIGeneratedReward).hdEnabled ? 'HD Enabled' : 'Standard'}
                        </p>
                      </div>
                    </div>
                    {(selectedReward as AIGeneratedReward).aiType === 'deep_shift' && <div className="grid grid-cols-2 gap-3" data-id="element-23886">
                        <div className="p-3 bg-gray-100/70 dark:bg-gray-800/50 backdrop-blur-sm rounded-lg border border-gray-300/50 dark:border-purple-500/20 relative overflow-hidden" data-id="element-23887">
                          {/* Background glow */}
                          <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/diamond-upholstery.png')] opacity-5" data-id="element-23888"></div>
                          <div className="flex items-center gap-1.5 mb-1 relative z-10" data-id="element-23889">
                            <Eye size={14} className="text-blue-600 dark:text-blue-400 animate-pulse-subtle" data-id="element-23890" />
                            <h4 className="text-sm font-medium text-gray-900 dark:text-white" data-id="element-23891">
                              Background
                            </h4>
                          </div>
                          <p className="text-sm text-gray-700 dark:text-gray-300 relative z-10" data-id="element-23892">
                            {(selectedReward as AIGeneratedReward).preserveBackground ? 'Preserved' : 'Transformed'}
                          </p>
                        </div>
                        <div className="p-3 bg-gray-100/70 dark:bg-gray-800/50 backdrop-blur-sm rounded-lg border border-gray-300/50 dark:border-purple-500/20 relative overflow-hidden" data-id="element-23893">
                          {/* Background glow */}
                          <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/diamond-upholstery.png')] opacity-5" data-id="element-23894"></div>
                          <div className="flex items-center gap-1.5 mb-1 relative z-10" data-id="element-23895">
                            <Film size={14} className="text-green-600 dark:text-green-400 animate-pulse-subtle" data-id="element-23896" />
                            <h4 className="text-sm font-medium text-gray-900 dark:text-white" data-id="element-23897">
                              Motion
                            </h4>
                          </div>
                          <p className="text-sm text-gray-700 dark:text-gray-300 relative z-10" data-id="element-23898">
                            {(selectedReward as AIGeneratedReward).motionSmooth ? 'Smooth' : 'Standard'}
                          </p>
                        </div>
                      </div>}
                  </div>
                </div>}
              {/* Tips section for AI rewards */}
              {selectedReward.type === 'ai_generated' && <div className="mt-4" data-id="element-23899">
                  <div className="p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800/30 rounded-lg flex items-start gap-2 backdrop-blur-sm relative overflow-hidden" data-id="element-23900">
                    {/* Background glow */}
                    <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/diamond-upholstery.png')] opacity-5" data-id="element-23901"></div>
                    <Info size={16} className="text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0 relative z-10" data-id="element-23902" />
                    <div className="relative z-10" data-id="element-23903">
                      <p className="text-sm font-medium text-blue-800 dark:text-blue-300 mb-1" data-id="element-23904">
                        Pro tip
                      </p>
                      <p className="text-sm text-blue-700 dark:text-blue-400" data-id="element-23905">
                        {(selectedReward as AIGeneratedReward).aiType === 'deep_shift' ? 'Try using DeepShift with different lighting conditions for best results.' : (selectedReward as AIGeneratedReward).aiType === 'face_shift' ? 'For better FaceShift results, use clear front-facing images with good lighting.' : (selectedReward as AIGeneratedReward).aiType === 'fantasy' ? 'Detailed prompts with specific settings and moods create more immersive fantasy scenarios.' : 'Add personality traits in your prompt to create more unique and personalized angel avatars.'}
                      </p>
                    </div>
                  </div>
                </div>}
            </div>
          </div>
        </div>
      </div>;
  };
  return <div className="w-full min-h-screen bg-gradient-to-b from-indigo-50 to-white dark:from-indigo-950 dark:to-black text-gray-900 dark:text-white relative" data-id="element-23906">
      {/* Background pattern */}
      <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/diamond-upholstery.png')] opacity-5 pointer-events-none" data-id="element-23907"></div>
      {/* Floating particles */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none" data-id="element-23908">
        {Array.from({
        length: 30
      }).map((_, i) => <div key={`bg-particle-${i}`} className="absolute w-1 h-1 rounded-full bg-purple-400/40 dark:bg-white/40 animate-float-particle" style={{
        top: `${Math.random() * 100}%`,
        left: `${Math.random() * 100}%`,
        animationDelay: `${Math.random() * 5}s`,
        animationDuration: `${Math.random() * 10 + 10}s`,
        '--random-x': `${Math.random() * 40 - 20}px`
      } as React.CSSProperties} data-id="element-23909"></div>)}
      </div>
      <div className="relative z-10" data-id="element-23910">
        <div className="mb-6" data-id="element-23911">
          <h1 className="text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-pink-600 dark:from-purple-400 dark:to-pink-400 mb-2 drop-shadow-[0_0_8px_rgba(168,85,247,0.5)]" data-id="element-23912">
            Reward Hub
          </h1>
          <p className="text-gray-700 dark:text-gray-300" data-id="element-23913">
            View and manage all your earned rewards
          </p>
        </div>
        {/* Main panel tabs */}
        <div className="mb-6" data-id="element-23914">
          <Tabs tabs={[{
          id: 'achievements',
          label: 'Achievements'
        }, {
          id: 'claimables',
          label: 'Claimables'
        }, {
          id: 'collections',
          label: 'Collections'
        }, {
          id: 'history',
          label: 'History'
        }]} activeTab={activePanelTab} onTabChange={tab => setActivePanelTab(tab as any)} data-id="element-23915" />
        </div>
        {/* Panel content */}
        <div className="mb-8 bg-white/70 dark:bg-gray-900/30 backdrop-blur-sm rounded-xl border border-gray-200/50 dark:border-purple-500/20 p-4 shadow-[0_0_20px_rgba(168,85,247,0.1)] relative overflow-hidden" data-id="element-23916">
          {/* Background pattern */}
          <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/diamond-upholstery.png')] opacity-5 pointer-events-none" data-id="element-23917"></div>
          {activePanelTab === 'achievements' && <AchievementsMini data-id="element-23918" />}
          {activePanelTab === 'claimables' && <Claimables data-id="element-23919" />}
          {activePanelTab === 'collections' && <Collections data-id="element-23920" />}
          {activePanelTab === 'history' && <History data-id="element-23921" />}
        </div>
        {/* Tabs */}
        <div className="mb-6 overflow-x-auto scrollbar-hide" data-id="element-23922">
          <div className="flex space-x-2 min-w-max p-1 bg-white/70 dark:bg-gray-900/30 backdrop-blur-sm rounded-lg border border-gray-200/50 dark:border-purple-500/20" data-id="element-23923">
            <button className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-300 relative overflow-hidden ${activeTab === 'all' ? 'text-gray-900 dark:text-white bg-gradient-to-r from-purple-100/80 to-pink-100/80 dark:from-purple-500/20 dark:to-pink-500/20 shadow-[0_0_10px_rgba(168,85,247,0.2)]' : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100/80 dark:hover:bg-gray-800/30'}`} onClick={() => handleTabChange('all')} data-id="element-23924">
              {/* Background pattern for active tab */}
              {activeTab === 'all' && <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/diamond-upholstery.png')] opacity-10" data-id="element-23925"></div>}
              {/* Glowing underline for active tab */}
              {activeTab === 'all' && <div className="absolute bottom-0 left-2 right-2 h-[2px] bg-gradient-to-r from-purple-500 to-pink-500 rounded-full shadow-[0_0_8px_rgba(168,85,247,0.5)]" data-id="element-23926"></div>}
              <span className="relative z-10" data-id="element-23927">All Rewards</span>
            </button>
            <button className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-300 flex items-center gap-1.5 relative overflow-hidden ${activeTab === 'tokens' ? 'text-gray-900 dark:text-white bg-gradient-to-r from-purple-100/80 to-pink-100/80 dark:from-purple-500/20 dark:to-pink-500/20 shadow-[0_0_10px_rgba(168,85,247,0.2)]' : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100/80 dark:hover:bg-gray-800/30'}`} onClick={() => handleTabChange('tokens')} data-id="element-23928">
              {/* Background pattern for active tab */}
              {activeTab === 'tokens' && <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/diamond-upholstery.png')] opacity-10" data-id="element-23929"></div>}
              {/* Glowing underline for active tab */}
              {activeTab === 'tokens' && <div className="absolute bottom-0 left-2 right-2 h-[2px] bg-gradient-to-r from-purple-500 to-pink-500 rounded-full shadow-[0_0_8px_rgba(168,85,247,0.5)]" data-id="element-23930"></div>}
              <Gift size={14} className={activeTab === 'tokens' ? 'text-pink-500 dark:text-pink-400' : ''} data-id="element-23931" />
              <span className="relative z-10" data-id="element-23932">Tokens</span>
            </button>
            <button className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-300 flex items-center gap-1.5 relative overflow-hidden ${activeTab === 'badges' ? 'text-gray-900 dark:text-white bg-gradient-to-r from-purple-100/80 to-pink-100/80 dark:from-purple-500/20 dark:to-pink-500/20 shadow-[0_0_10px_rgba(168,85,247,0.2)]' : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100/80 dark:hover:bg-gray-800/30'}`} onClick={() => handleTabChange('badges')} data-id="element-23933">
              {/* Background pattern for active tab */}
              {activeTab === 'badges' && <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/diamond-upholstery.png')] opacity-10" data-id="element-23934"></div>}
              {/* Glowing underline for active tab */}
              {activeTab === 'badges' && <div className="absolute bottom-0 left-2 right-2 h-[2px] bg-gradient-to-r from-purple-500 to-pink-500 rounded-full shadow-[0_0_8px_rgba(168,85,247,0.5)]" data-id="element-23935"></div>}
              <Trophy size={14} className={activeTab === 'badges' ? 'text-amber-500 dark:text-amber-400' : ''} data-id="element-23936" />
              <span className="relative z-10" data-id="element-23937">Badges</span>
            </button>
            <button className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-300 flex items-center gap-1.5 relative overflow-hidden ${activeTab === 'skins' ? 'text-gray-900 dark:text-white bg-gradient-to-r from-purple-100/80 to-pink-100/80 dark:from-purple-500/20 dark:to-pink-500/20 shadow-[0_0_10px_rgba(168,85,247,0.2)]' : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100/80 dark:hover:bg-gray-800/30'}`} onClick={() => handleTabChange('skins')} data-id="element-23938">
              {/* Background pattern for active tab */}
              {activeTab === 'skins' && <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/diamond-upholstery.png')] opacity-10" data-id="element-23939"></div>}
              {/* Glowing underline for active tab */}
              {activeTab === 'skins' && <div className="absolute bottom-0 left-2 right-2 h-[2px] bg-gradient-to-r from-purple-500 to-pink-500 rounded-full shadow-[0_0_8px_rgba(168,85,247,0.5)]" data-id="element-23940"></div>}
              <Star size={14} className={activeTab === 'skins' ? 'text-blue-500 dark:text-blue-400' : ''} data-id="element-23941" />
              <span className="relative z-10" data-id="element-23942">Angel Skins</span>
            </button>
            <button className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-300 flex items-center gap-1.5 relative overflow-hidden ${activeTab === 'content' ? 'text-gray-900 dark:text-white bg-gradient-to-r from-purple-100/80 to-pink-100/80 dark:from-purple-500/20 dark:to-pink-500/20 shadow-[0_0_10px_rgba(168,85,247,0.2)]' : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100/80 dark:hover:bg-gray-800/30'}`} onClick={() => handleTabChange('content')} data-id="element-23943">
              {/* Background pattern for active tab */}
              {activeTab === 'content' && <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/diamond-upholstery.png')] opacity-10" data-id="element-23944"></div>}
              {/* Glowing underline for active tab */}
              {activeTab === 'content' && <div className="absolute bottom-0 left-2 right-2 h-[2px] bg-gradient-to-r from-purple-500 to-pink-500 rounded-full shadow-[0_0_8px_rgba(168,85,247,0.5)]" data-id="element-23945"></div>}
              <ImageIcon size={14} className={activeTab === 'content' ? 'text-green-500 dark:text-green-400' : ''} data-id="element-23946" />
              <span className="relative z-10" data-id="element-23947">Content</span>
            </button>
            <button className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-300 flex items-center gap-1.5 relative overflow-hidden ${activeTab === 'lootboxes' ? 'text-gray-900 dark:text-white bg-gradient-to-r from-purple-100/80 to-pink-100/80 dark:from-purple-500/20 dark:to-pink-500/20 shadow-[0_0_10px_rgba(168,85,247,0.2)]' : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100/80 dark:hover:bg-gray-800/30'}`} onClick={() => handleTabChange('lootboxes')} data-id="element-23948">
              {/* Background pattern for active tab */}
              {activeTab === 'lootboxes' && <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/diamond-upholstery.png')] opacity-10" data-id="element-23949"></div>}
              {/* Glowing underline for active tab */}
              {activeTab === 'lootboxes' && <div className="absolute bottom-0 left-2 right-2 h-[2px] bg-gradient-to-r from-purple-500 to-pink-500 rounded-full shadow-[0_0_8px_rgba(168,85,247,0.5)]" data-id="element-23950"></div>}
              <Gift size={14} className={activeTab === 'lootboxes' ? 'text-yellow-500 dark:text-yellow-400' : ''} data-id="element-23951" />
              <span className="relative z-10" data-id="element-23952">Lootboxes</span>
            </button>
            <button className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-300 flex items-center gap-1.5 relative overflow-hidden ${activeTab === 'ai_generated' ? 'bg-gradient-to-r from-purple-200/80 to-pink-200/80 dark:from-purple-500/30 dark:to-pink-500/30 text-gray-900 dark:text-white shadow-[0_0_15px_rgba(168,85,247,0.3)]' : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100/80 dark:hover:bg-gray-800/30'}`} onClick={() => handleTabChange('ai_generated')} data-id="element-23953">
              {/* Background pattern for active tab */}
              {activeTab === 'ai_generated' && <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/diamond-upholstery.png')] opacity-10" data-id="element-23954"></div>}
              {/* Glowing underline for active tab */}
              {activeTab === 'ai_generated' && <div className="absolute bottom-0 left-2 right-2 h-[2px] bg-gradient-to-r from-purple-500 to-pink-500 rounded-full shadow-[0_0_8px_rgba(168,85,247,0.5)]" data-id="element-23955"></div>}
              <Wand2 size={14} className={activeTab === 'ai_generated' ? 'text-pink-500 dark:text-pink-400 animate-pulse-subtle' : ''} data-id="element-23956" />
              <span className="relative z-10" data-id="element-23957">AI Creations</span>
              <span className="ml-1 bg-gradient-to-r from-purple-100 to-pink-100 dark:from-purple-500/40 dark:to-pink-500/40 backdrop-blur-sm px-1.5 py-0.5 rounded-full text-xs text-purple-800 dark:text-white" data-id="element-23958">
                New!
              </span>
            </button>
            <button className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-300 flex items-center gap-1.5 relative overflow-hidden ${activeTab === 'dickpic_reviews' ? 'bg-gradient-to-r from-purple-200/80 to-pink-200/80 dark:from-purple-500/30 dark:to-pink-500/30 text-gray-900 dark:text-white shadow-[0_0_15px_rgba(168,85,247,0.3)]' : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100/80 dark:hover:bg-gray-800/30'}`} onClick={() => handleTabChange('dickpic_reviews')} data-id="element-23959">
              {/* Background pattern for active tab */}
              {activeTab === 'dickpic_reviews' && <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/diamond-upholstery.png')] opacity-10" data-id="element-23960"></div>}
              {/* Glowing underline for active tab */}
              {activeTab === 'dickpic_reviews' && <div className="absolute bottom-0 left-2 right-2 h-[2px] bg-gradient-to-r from-purple-500 to-pink-500 rounded-full shadow-[0_0_8px_rgba(168,85,247,0.5)]" data-id="element-23961"></div>}
              <Star size={14} className={activeTab === 'dickpic_reviews' ? 'text-pink-500 dark:text-pink-400 animate-pulse-subtle' : ''} data-id="element-23962" />
              <span className="relative z-10" data-id="element-23963">Dickpic Reviews</span>
              <span className="ml-1 bg-gradient-to-r from-pink-100 to-rose-100 dark:from-pink-500/40 dark:to-rose-500/40 backdrop-blur-sm px-1.5 py-0.5 rounded-full text-xs text-pink-800 dark:text-white" data-id="element-23964">
                Hot!
              </span>
            </button>
          </div>
        </div>
        {/* Search and filters */}
        <div className="mb-6" data-id="element-23965">
          <div className="flex flex-col sm:flex-row gap-3" data-id="element-23966">
            <div className="relative flex-1" data-id="element-23967">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-23968">
                <Search size={16} className="text-purple-500 dark:text-purple-400" data-id="element-23969" />
              </div>
              <input type="text" placeholder="Search rewards..." value={searchQuery} onChange={handleSearch} className="w-full pl-10 pr-4 py-2 border border-gray-300/50 dark:border-purple-500/30 rounded-lg bg-white/80 dark:bg-gray-900/50 backdrop-blur-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-purple-500/50 focus:border-purple-500/50 placeholder-gray-500 dark:placeholder-gray-500" data-id="element-23970" />
            </div>
            <button className="flex items-center justify-center gap-2 px-4 py-2 bg-white/80 dark:bg-gray-800/70 backdrop-blur-sm rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-100/80 dark:hover:bg-gray-700/70 transition-colors border border-gray-300/50 dark:border-purple-500/20" onClick={handleFilterToggle} data-id="element-23971">
              <Filter size={16} className="text-purple-500 dark:text-purple-400" data-id="element-23972" />
              <span data-id="element-23973">Filters</span>
              {showFilters ? <ChevronUp size={16} className="text-purple-500 dark:text-purple-400" data-id="element-23974" /> : <ChevronDown size={16} className="text-purple-500 dark:text-purple-400" data-id="element-23975" />}
            </button>
          </div>
          {/* Filter options */}
          {showFilters && <div className="mt-3 p-4 bg-white/80 dark:bg-gray-900/50 backdrop-blur-sm rounded-lg border border-gray-300/50 dark:border-purple-500/20 animate-in slide-in-from-top duration-300 shadow-[0_0_15px_rgba(168,85,247,0.1)] relative overflow-hidden" data-id="element-23976">
              {/* Background pattern */}
              <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/diamond-upholstery.png')] opacity-5" data-id="element-23977"></div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 relative z-10" data-id="element-23978">
                <div data-id="element-23979">
                  <h3 className="text-sm font-medium text-purple-700 dark:text-purple-300 mb-2" data-id="element-23980">
                    Status
                  </h3>
                  <div className="space-y-2" data-id="element-23981">
                    <label className="flex items-center gap-2 cursor-pointer" data-id="element-23982">
                      <input type="checkbox" checked={filters.claimed} onChange={e => handleFilterChange('claimed', e.target.checked)} className="rounded text-purple-600 focus:ring-purple-500 bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-700" data-id="element-23983" />
                      <span className="text-sm text-gray-700 dark:text-gray-300" data-id="element-23984">
                        Claimed
                      </span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer" data-id="element-23985">
                      <input type="checkbox" checked={filters.unclaimed} onChange={e => handleFilterChange('unclaimed', e.target.checked)} className="rounded text-purple-600 focus:ring-purple-500 bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-700" data-id="element-23986" />
                      <span className="text-sm text-gray-700 dark:text-gray-300" data-id="element-23987">
                        Unclaimed
                      </span>
                    </label>
                  </div>
                </div>
                <div data-id="element-23988">
                  <h3 className="text-sm font-medium text-purple-700 dark:text-purple-300 mb-2" data-id="element-23989">
                    Sort By
                  </h3>
                  <div className="space-y-2" data-id="element-23990">
                    <label className="flex items-center gap-2 cursor-pointer" data-id="element-23991">
                      <input type="radio" name="sort" checked={filters.newest} onChange={() => {
                    handleFilterChange('newest', true);
                    handleFilterChange('oldest', false);
                  }} className="rounded-full text-purple-600 focus:ring-purple-500 bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-700" data-id="element-23992" />
                      <span className="text-sm text-gray-700 dark:text-gray-300" data-id="element-23993">
                        Newest First
                      </span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer" data-id="element-23994">
                      <input type="radio" name="sort" checked={filters.oldest} onChange={() => {
                    handleFilterChange('newest', false);
                    handleFilterChange('oldest', true);
                  }} className="rounded-full text-purple-600 focus:ring-purple-500 bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-700" data-id="element-23995" />
                      <span className="text-sm text-gray-700 dark:text-gray-300" data-id="element-23996">
                        Oldest First
                      </span>
                    </label>
                  </div>
                </div>
                {(activeTab === 'all' || activeTab === 'skins' || activeTab === 'badges') && <div data-id="element-23997">
                    <h3 className="text-sm font-medium text-purple-700 dark:text-purple-300 mb-2" data-id="element-23998">
                      Rarity
                    </h3>
                    <div className="space-y-2" data-id="element-23999">
                      <label className="flex items-center gap-2 cursor-pointer" data-id="element-24000">
                        <input type="checkbox" checked={filters.rarity.common} onChange={e => handleRarityFilterChange('common', e.target.checked)} className="rounded text-purple-600 focus:ring-purple-500 bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-700" data-id="element-24001" />
                        <span className="text-sm text-gray-700 dark:text-gray-300" data-id="element-24002">
                          Common
                        </span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer" data-id="element-24003">
                        <input type="checkbox" checked={filters.rarity.uncommon} onChange={e => handleRarityFilterChange('uncommon', e.target.checked)} className="rounded text-purple-600 focus:ring-purple-500 bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-700" data-id="element-24004" />
                        <span className="text-sm text-gray-700 dark:text-gray-300" data-id="element-24005">
                          Uncommon
                        </span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer" data-id="element-24006">
                        <input type="checkbox" checked={filters.rarity.rare} onChange={e => handleRarityFilterChange('rare', e.target.checked)} className="rounded text-purple-600 focus:ring-purple-500 bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-700" data-id="element-24007" />
                        <span className="text-sm text-gray-700 dark:text-gray-300" data-id="element-24008">
                          Rare
                        </span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer" data-id="element-24009">
                        <input type="checkbox" checked={filters.rarity.legendary} onChange={e => handleRarityFilterChange('legendary', e.target.checked)} className="rounded text-purple-600 focus:ring-purple-500 bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-700" data-id="element-24010" />
                        <span className="text-sm text-gray-700 dark:text-gray-300" data-id="element-24011">
                          Legendary
                        </span>
                      </label>
                    </div>
                  </div>}
              </div>
            </div>}
        </div>
        {/* Sensitive Content Toggle */}
        {activeTab === 'dickpic_reviews' && <div className="mb-6 p-4 bg-white/80 dark:bg-gray-900/50 backdrop-blur-sm rounded-lg border border-gray-300/50 dark:border-purple-500/20 shadow-[0_0_15px_rgba(168,85,247,0.1)] relative overflow-hidden" data-id="element-24012">
            <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/diamond-upholstery.png')] opacity-5" data-id="element-24013"></div>
            <div className="flex items-center justify-between relative z-10" data-id="element-24014">
              <div className="flex items-center gap-3" data-id="element-24015">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-pink-100 dark:bg-pink-900/30 text-pink-600 dark:text-pink-400" data-id="element-24016">
                  {showSensitiveContent ? <Eye size={20} data-id="element-24017" /> : <EyeOff size={20} data-id="element-24018" />}
                </div>
                <div data-id="element-24019">
                  <h3 className="font-medium text-gray-900 dark:text-white" data-id="element-24020">
                    Sensitive Content
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400" data-id="element-24021">
                    {showSensitiveContent ? 'Sensitive content is visible' : 'Sensitive content is hidden'}
                  </p>
                </div>
              </div>
              <button className="px-4 py-2 bg-gradient-to-r from-pink-100 to-purple-100 dark:from-pink-600 dark:to-purple-600 text-pink-800 dark:text-white rounded-lg text-sm font-medium hover:shadow-md transition-all" onClick={toggleSensitiveContent} data-id="element-24022">
                {showSensitiveContent ? 'Hide Sensitive Content' : 'Show Sensitive Content'}
              </button>
            </div>
          </div>}
        {/* Ritual Rewards */}
        {activeTab === 'all' && <div className="mb-8" data-id="element-24023">
            <div className="flex items-center justify-between mb-4" data-id="element-24024">
              <h2 className="text-xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-amber-600 to-yellow-500 dark:from-amber-400 dark:to-yellow-300 flex items-center gap-2" data-id="element-24025">
                <Trophy size={20} className="text-amber-500" data-id="element-24026" />
                Ritual Rewards
              </h2>
              <button className="text-sm text-purple-600 dark:text-purple-400 hover:text-purple-700 dark:hover:text-purple-300 flex items-center gap-1 group" onClick={() => handleNavigation('rituals')} data-id="element-24027">
                <span className="group-hover:underline" data-id="element-24028">View Rituals</span>
                <ChevronRight size={16} className="group-hover:translate-x-0.5 transition-transform" data-id="element-24029" />
              </button>
            </div>
            <div className="bg-white/70 dark:bg-gray-900/30 backdrop-blur-sm rounded-xl border border-gray-200/50 dark:border-purple-500/20 p-4 shadow-[0_0_20px_rgba(168,85,247,0.1)] relative overflow-hidden" data-id="element-24030">
              {/* Background pattern */}
              <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/diamond-upholstery.png')] opacity-5 pointer-events-none" data-id="element-24031"></div>
              <RitualRewards data-id="element-24032" />
            </div>
          </div>}
        {/* Dickpic Reviews Featured Section */}
        {activeTab === 'all' && <div className="mb-8" data-id="element-24033">
            <div className="flex items-center justify-between mb-4" data-id="element-24034">
              <h2 className="text-xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-pink-600 to-rose-600 dark:from-pink-400 dark:to-rose-400 flex items-center gap-2" data-id="element-24035">
                <Star size={20} className="text-pink-500 dark:text-pink-400" data-id="element-24036" />
                Dickpic Reviews
                <span className="ml-1 px-2 py-0.5 bg-gradient-to-r from-pink-100 to-rose-100 dark:from-pink-500/30 dark:to-rose-500/30 text-pink-800 dark:text-white text-xs rounded-full" data-id="element-24037">
                  Hot!
                </span>
              </h2>
              <button className="text-sm text-purple-600 dark:text-purple-400 hover:text-purple-700 dark:hover:text-purple-300 flex items-center gap-1 group" onClick={() => handleTabChange('dickpic_reviews')} data-id="element-24038">
                <span className="group-hover:underline" data-id="element-24039">View All</span>
                <ChevronRight size={16} className="group-hover:translate-x-0.5 transition-transform" data-id="element-24040" />
              </button>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4" data-id="element-24041">
              {rewards.filter(reward => reward.type === 'dickpic_review').sort((a, b) => b.dateEarned.getTime() - a.dateEarned.getTime()).slice(0, 5).map(renderRewardCard)}
            </div>
          </div>}
        {/* AI Creations Featured Section */}
        {activeTab === 'all' && <div className="mb-8" data-id="element-24042">
            <div className="flex items-center justify-between mb-4" data-id="element-24043">
              <h2 className="text-xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-pink-600 dark:from-purple-400 dark:to-pink-400 flex items-center gap-2" data-id="element-24044">
                <Wand2 size={20} className="text-pink-500 dark:text-pink-400 animate-pulse-subtle" data-id="element-24045" />
                AI Creations
                <span className="ml-1 px-2 py-0.5 bg-gradient-to-r from-purple-100 to-pink-100 dark:from-purple-500/30 dark:to-pink-500/30 text-purple-800 dark:text-white text-xs rounded-full" data-id="element-24046">
                  New!
                </span>
              </h2>
              <button className="text-sm text-purple-600 dark:text-purple-400 hover:text-purple-700 dark:hover:text-purple-300 flex items-center gap-1 group" onClick={() => handleTabChange('ai_generated')} data-id="element-24047">
                <span className="group-hover:underline" data-id="element-24048">View All</span>
                <ChevronRight size={16} className="group-hover:translate-x-0.5 transition-transform" data-id="element-24049" />
              </button>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4" data-id="element-24050">
              {rewards.filter(reward => reward.type === 'ai_generated').sort((a, b) => b.dateEarned.getTime() - a.dateEarned.getTime()).slice(0, 5).map(renderRewardCard)}
            </div>
          </div>}
        {/* Main rewards grid */}
        <div data-id="element-24051">
          {activeTab === 'ai_generated' && <div className="mb-6 p-4 bg-gradient-to-r from-purple-100/50 to-pink-100/50 dark:from-purple-500/10 dark:to-pink-500/10 border border-gray-200/50 dark:border-purple-500/20 rounded-lg backdrop-blur-sm shadow-[0_0_15px_rgba(168,85,247,0.1)] relative overflow-hidden" data-id="element-24052">
              {/* Background pattern */}
              <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/diamond-upholstery.png')] opacity-5" data-id="element-24053"></div>
              <div className="flex items-start gap-3 relative z-10" data-id="element-24054">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-gradient-to-r from-purple-500 to-pink-500 text-white shadow-[0_0_15px_rgba(168,85,247,0.3)]" data-id="element-24055">
                  <Wand2 size={20} className="animate-pulse-subtle" data-id="element-24056" />
                </div>
                <div data-id="element-24057">
                  <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-1" data-id="element-24058">
                    AI Creation Rewards
                  </h3>
                  <p className="text-gray-700 dark:text-gray-300" data-id="element-24059">
                    View all your DeepShift, FaceShift, Fantasy Scenarios, and
                    Angel Avatars. Click on any item to view details, download,
                    or share.
                  </p>
                  <div className="mt-3 flex flex-wrap gap-2" data-id="element-24060">
                    <div className="flex items-center gap-1.5 px-3 py-1.5 bg-white/70 dark:bg-gray-800/70 backdrop-blur-sm rounded-full text-sm border border-gray-200/50 dark:border-purple-500/20 text-gray-700 dark:text-gray-300" data-id="element-24061">
                      <Film size={16} className="text-blue-500 dark:text-blue-400" data-id="element-24062" />
                      <span data-id="element-24063">DeepShift Videos</span>
                    </div>
                    <div className="flex items-center gap-1.5 px-3 py-1.5 bg-white/70 dark:bg-gray-800/70 backdrop-blur-sm rounded-full text-sm border border-gray-200/50 dark:border-purple-500/20 text-gray-700 dark:text-gray-300" data-id="element-24064">
                      <User size={16} className="text-green-500 dark:text-green-400" data-id="element-24065" />
                      <span data-id="element-24066">FaceShift Transformations</span>
                    </div>
                    <div className="flex items-center gap-1.5 px-3 py-1.5 bg-white/70 dark:bg-gray-800/70 backdrop-blur-sm rounded-full text-sm border border-gray-200/50 dark:border-purple-500/20 text-gray-700 dark:text-gray-300" data-id="element-24067">
                      <Sparkles size={16} className="text-pink-500 dark:text-pink-400" data-id="element-24068" />
                      <span data-id="element-24069">Unleashed Fantasies</span>
                    </div>
                    <div className="flex items-center gap-1.5 px-3 py-1.5 bg-white/70 dark:bg-gray-800/70 backdrop-blur-sm rounded-full text-sm border border-gray-200/50 dark:border-purple-500/20 text-gray-700 dark:text-gray-300" data-id="element-24070">
                      <Heart size={16} className="text-red-500 dark:text-red-400" data-id="element-24071" />
                      <span data-id="element-24072">Unleashed Angels</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>}
          {activeTab === 'dickpic_reviews' && <div className="mb-6 p-4 bg-gradient-to-r from-pink-100/50 to-rose-100/50 dark:from-pink-500/10 dark:to-rose-500/10 border border-gray-200/50 dark:border-purple-500/20 rounded-lg backdrop-blur-sm shadow-[0_0_15px_rgba(236,72,153,0.1)] relative overflow-hidden" data-id="element-24073">
              {/* Background pattern */}
              <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/diamond-upholstery.png')] opacity-5" data-id="element-24074"></div>
              <div className="flex items-start gap-3 relative z-10" data-id="element-24075">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-gradient-to-r from-pink-500 to-rose-500 text-white shadow-[0_0_15px_rgba(236,72,153,0.3)]" data-id="element-24076">
                  <Star size={20} className="animate-pulse-subtle" data-id="element-24077" />
                </div>
                <div data-id="element-24078">
                  <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-1" data-id="element-24079">
                    Dickpic Review Rewards
                  </h3>
                  <p className="text-gray-700 dark:text-gray-300" data-id="element-24080">
                    View all your personal ratings, detailed feedback, and
                    fantasy responses from your favorite angels. Your privacy is
                    always protected.
                  </p>
                  <div className="mt-3 flex flex-wrap gap-2" data-id="element-24081">
                    <div className="flex items-center gap-1.5 px-3 py-1.5 bg-white/70 dark:bg-gray-800/70 backdrop-blur-sm rounded-full text-sm border border-gray-200/50 dark:border-purple-500/20 text-gray-700 dark:text-gray-300" data-id="element-24082">
                      <Star size={16} className="text-amber-500 dark:text-amber-400" data-id="element-24083" />
                      <span data-id="element-24084">Personal Ratings</span>
                    </div>
                    <div className="flex items-center gap-1.5 px-3 py-1.5 bg-white/70 dark:bg-gray-800/70 backdrop-blur-sm rounded-full text-sm border border-gray-200/50 dark:border-purple-500/20 text-gray-700 dark:text-gray-300" data-id="element-24085">
                      <Sparkles size={16} className="text-blue-500 dark:text-blue-400" data-id="element-24086" />
                      <span data-id="element-24087">AI Analysis</span>
                    </div>
                    <div className="flex items-center gap-1.5 px-3 py-1.5 bg-white/70 dark:bg-gray-800/70 backdrop-blur-sm rounded-full text-sm border border-gray-200/50 dark:border-purple-500/20 text-gray-700 dark:text-gray-300" data-id="element-24088">
                      <Heart size={16} className="text-pink-500 dark:text-pink-400" data-id="element-24089" />
                      <span data-id="element-24090">Fantasy Responses</span>
                    </div>
                    <div className="flex items-center gap-1.5 px-3 py-1.5 bg-white/70 dark:bg-gray-800/70 backdrop-blur-sm rounded-full text-sm border border-gray-200/50 dark:border-purple-500/20 text-gray-700 dark:text-gray-300" data-id="element-24091">
                      <Shield size={16} className="text-green-500 dark:text-green-400" data-id="element-24092" />
                      <span data-id="element-24093">Privacy Protected</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>}
          {filteredRewards.length > 0 ? <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4" data-id="element-24094">
              {filteredRewards.map(renderRewardCard)}
            </div> : <div className="flex flex-col items-center justify-center py-12 px-4 bg-white/70 dark:bg-gray-900/30 backdrop-blur-sm rounded-lg border border-gray-200/50 dark:border-purple-500/20 shadow-[0_0_15px_rgba(168,85,247,0.1)] relative overflow-hidden" data-id="element-24095">
              {/* Background pattern */}
              <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/diamond-upholstery.png')] opacity-5" data-id="element-24096"></div>
              <div className="w-16 h-16 flex items-center justify-center rounded-full bg-gray-100/70 dark:bg-gray-800/70 border border-gray-200/50 dark:border-purple-500/20 mb-4 shadow-[0_0_15px_rgba(168,85,247,0.2)] relative z-10" data-id="element-24097">
                {activeTab === 'ai_generated' ? <Wand2 size={24} className="text-purple-500 dark:text-purple-400" data-id="element-24098" /> : activeTab === 'tokens' ? <Gift size={24} className="text-purple-500 dark:text-purple-400" data-id="element-24099" /> : activeTab === 'badges' ? <Trophy size={24} className="text-purple-500 dark:text-purple-400" data-id="element-24100" /> : activeTab === 'skins' ? <Star size={24} className="text-purple-500 dark:text-purple-400" data-id="element-24101" /> : activeTab === 'content' ? <ImageIcon size={24} className="text-purple-500 dark:text-purple-400" data-id="element-24102" /> : activeTab === 'lootboxes' ? <Gift size={24} className="text-purple-500 dark:text-purple-400" data-id="element-24103" /> : activeTab === 'dickpic_reviews' ? <Star size={24} className="text-pink-500 dark:text-pink-400" data-id="element-24104" /> : <Heart size={24} className="text-purple-500 dark:text-purple-400" data-id="element-24105" />}
              </div>
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-1 relative z-10" data-id="element-24106">
                No rewards found
              </h3>
              <p className="text-gray-600 dark:text-gray-400 text-center max-w-md relative z-10" data-id="element-24107">
                {searchQuery ? `No rewards matching "${searchQuery}" were found. Try a different search term.` : `You don't have any ${activeTab === 'all' ? '' : activeTab.replace('_', ' ')} rewards yet. Complete rituals to earn rewards!`}
              </p>
              {activeTab === 'ai_generated' && !searchQuery && <div className="mt-4 relative z-10" data-id="element-24108">
                  <button onClick={() => {
              // Dispatch event to open DeepShift modal
              const deepShiftEvent = new CustomEvent('openDeepShiftModal', {});
              document.dispatchEvent(deepShiftEvent);
            }} className="px-4 py-2 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-lg text-sm font-medium hover:shadow-[0_0_15px_rgba(168,85,247,0.3)] transition-all flex items-center gap-2 relative overflow-hidden group" data-id="element-24109">
                    {/* Button hover effect */}
                    <div className="absolute inset-0 bg-white/20 opacity-0 group-hover:opacity-100 transform translate-x-full group-hover:translate-x-0 transition-all duration-500" data-id="element-24110"></div>
                    <Wand2 size={16} className="relative z-10" data-id="element-24111" />
                    <span className="relative z-10" data-id="element-24112">Create with DeepShift</span>
                  </button>
                </div>}
              {activeTab === 'dickpic_reviews' && !searchQuery && <div className="mt-4 relative z-10" data-id="element-24113">
                  <button onClick={() => {
              // Dispatch event to open dickpic rating modal
              const ratingEvent = new CustomEvent('openDickpicRatingModal', {});
              document.dispatchEvent(ratingEvent);
            }} className="px-4 py-2 bg-gradient-to-r from-pink-500 to-rose-500 text-white rounded-lg text-sm font-medium hover:shadow-[0_0_15px_rgba(236,72,153,0.3)] transition-all flex items-center gap-2 relative overflow-hidden group" data-id="element-24114">
                    {/* Button hover effect */}
                    <div className="absolute inset-0 bg-white/20 opacity-0 group-hover:opacity-100 transform translate-x-full group-hover:translate-x-0 transition-all duration-500" data-id="element-24115"></div>
                    <Star size={16} className="relative z-10" data-id="element-24116" />
                    <span className="relative z-10" data-id="element-24117">Get Rated Now</span>
                  </button>
                </div>}
            </div>}
        </div>
        {/* Reward detail modal */}
        {selectedReward && renderRewardDetail()}
      </div>
    </div>;
};